import { clsx } from 'clsx';
import throttle from 'lodash/throttle';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { DynamicLink } from '@/components/DynamicLink';
import { GlobalSearchSkeleton } from '@/features/GlobalSearch/GlobalSearchSkeleton';
import { useIsLoggedIn } from '@/hooks/auth';
import { SidebarContext } from '@/providers/SidebarProvider';
import { pathname } from '@/utils/router';
import { usesDarkChrome, usesHomepageNav, usesSolidChrome } from '@/utils/page-chrome';
const GlobalSearch = dynamic(() => import('@/features/GlobalSearch/globalSearch').then(mod => mod.GlobalSearch), {
  ssr: false,
  loading: () => <GlobalSearchSkeleton />
});
import type { ReactNode } from 'react';
import { UBLogoMobile } from '../Logo';
interface TopBarHeaderProps {
  children: ReactNode;
}
export const TopBarHeader = ({
  children
}: TopBarHeaderProps) => {
  const [wasScrolled, setWasScrolled] = useState(false);
  const [hasScrolledPastAI, setHasScrolledPastAI] = useState(false);
  const {
    toggleSearch,
    isSidebarToggled
  } = useContext(SidebarContext);
  const router = useRouter();
  const isLoggedIn = useIsLoggedIn();
  const isHelpCenter = router.pathname.includes('/help-center');
  const isPricingPage = router.pathname.includes('/pricing');
  const hidesGlobalSearch = usesHomepageNav(router.pathname);
  const forcesDarkChrome = usesDarkChrome(router.pathname);
  const hasSolidChrome = usesSolidChrome(router.pathname);
  useEffect(() => {
    const onScroll = throttle(() => {
      setWasScrolled(window.scrollY > 0);

      // Check when scrolling past AI intro.
      if (pathname(router).includes('/playlist-generator')) {
        const aiPageTop = document.getElementById('ai-page-top');

        // > 0 if we haven't gone past the element yet.
        const aiPageTopRect = aiPageTop?.getBoundingClientRect().bottom;
        if (aiPageTopRect != null) {
          setHasScrolledPastAI(aiPageTopRect < 64);
        }
      }
    }, 16);

    // Initial check in case the page loads already scrolled
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [router]);
  const userLoggedOut = !isLoggedIn;
  const showBlueBg = router.isReady && pathname(router).includes('/playlist-generator') && !hasScrolledPastAI;
  return <header className={clsx('header-main group sticky right-0 z-30 rounded-none border-none px-4 py-2 shadow-none! transition md:px-container md:py-2.5 md:pr-3 lg:pr-4', '[.sidebar-toggled_&]:bg-white dark:[.sidebar-toggled_&]:bg-gray-950', userLoggedOut && 'logged-out', wasScrolled && 'shrink', showBlueBg && 'blue-bg',
  // `bg-transparent` also hides the homepage library links (see
  // TopBarContent), so a solid-chrome page never takes it.
  wasScrolled || hasSolidChrome ? 'bg-white dark:bg-gray-950' : 'bg-transparent',
  // The header sits in flow above the hero rather than over it, so on a
  // page that is dark in both themes it has to carry the hero's colour
  // itself — `bg-transparent` would show the white page behind.
  forcesDarkChrome && 'theme-dark bg-gray-950!', (isHelpCenter || isPricingPage) && 'bg-white dark:bg-gray-950',
  // Pricing draws its own logo from 600px up, so its header is
  // mobile-only rather than hidden outright (DEV-6606).
  isPricingPage && 'headerFixed:hidden',
  // Note: I removed the min-h-14 class because it was causing
  // issues with the zones navigation on desktop. This will re-introduce
  // the Uppbeat logo moving issue but it's a better trade-off.
  // Joel's sidebar PR #3139 will address this issue.
  'w-full')} data-sentry-component="TopBarHeader" data-sentry-source-file="TopBarHeader.tsx">
      <div className="flex w-full items-center gap-4">
        <div className={clsx('block shrink-0 [@media(min-width:600px)]:hidden')}>
          <DynamicLink aria-label="Uppbeat homepage" href="/" onClick={() => toggleSearch(false)} prefetch={false} data-sentry-element="DynamicLink" data-sentry-source-file="TopBarHeader.tsx">
            <UBLogoMobile data-sentry-element="UBLogoMobile" data-sentry-source-file="TopBarHeader.tsx" />
          </DynamicLink>
        </div>
        {/* min-h matches the 48px GlobalSearch pill so the header keeps the
            same height on the homepage, where the search is omitted
            (DEV-6657). */}
        <div className="grow md:min-h-12">
          {hidesGlobalSearch ? null : <GlobalSearch listenForOpenRequest />}
        </div>
        {children}
      </div>
    </header>;
};